import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useFb } from "../../utils/football";

const CURRENT_YEAR = 2026;
const YEARS = Array.from({ length: 60 }, (_, i) => CURRENT_YEAR - 13 - i); // 13..72 yrs old

/**
 * ProfileModal (Tier 2 profiling) — birth_year + city + gender, shown on first
 * squad save. Gender is optional/skippable. Controlled by parent (open/onClose).
 */
export const ProfileModal = ({ open, onClose }) => {
  const { dbUser, setDbUser } = useAuth();
  const fb = useFb();
  const [birthYear, setBirthYear] = useState("");
  const [city, setCity] = useState("");
  const [gender, setGender] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    const payload = {};
    if (birthYear) payload.birth_year = parseInt(birthYear, 10);
    if (city) payload.city = city.trim();
    if (gender) payload.gender = gender;
    try { if (Object.keys(payload).length) await fb.put("/users/profile", payload); } catch {}
    setDbUser({ ...dbUser, ...payload });
    setSaving(false);
    onClose(true);
  };

  return (
    <div className="football-theme fixed inset-0 z-[65] grid place-items-center bg-black/70 p-5" data-testid="profile-modal">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6">
        <h2 className="text-xl font-extrabold">Tell us a bit about you</h2>
        <p className="mt-1 text-sm text-muted-foreground">Quick one-time setup. Gender is optional.</p>

        <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-muted-foreground">Birth year</label>
        <select value={birthYear} onChange={(e) => setBirthYear(e.target.value)}
          className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-amber-400"
          data-testid="profile-birth-year">
          <option value="">Select…</option>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>

        <label className="mt-3 block text-xs font-bold uppercase tracking-wide text-muted-foreground">City</label>
        <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Dubai"
          className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-amber-400"
          data-testid="profile-city" />

        <label className="mt-3 block text-xs font-bold uppercase tracking-wide text-muted-foreground">Gender (optional)</label>
        <div className="mt-1 flex gap-2">
          {["male", "female", "other"].map((g) => (
            <button key={g} onClick={() => setGender(gender === g ? "" : g)}
              className={`flex-1 rounded-lg py-2 text-sm font-bold capitalize ${gender === g ? "wc-bg-gold text-slate-900" : "bg-secondary"}`}
              data-testid={`profile-gender-${g}`}>{g}</button>
          ))}
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={() => onClose(false)} className="flex-1 rounded-xl bg-secondary py-3 font-bold" data-testid="profile-skip">Skip</button>
          <button onClick={save} disabled={saving} className="flex-1 rounded-xl wc-bg-gold py-3 font-extrabold text-slate-900 disabled:opacity-60" data-testid="profile-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
